import clsx from 'clsx';
import { useChatStore } from '@/stores/chat';

/**
 * Pure read-only banner that surfaces the WebSocket connection state.
 * Renders null while connected so it doesn't reserve vertical space in the
 * message area when everything is healthy.
 */
export default function ConnectionStatusBanner() {
  const wsStatus = useChatStore((s) => s.wsStatus);

  if (wsStatus === 'open') return null;

  const config = (() => {
    switch (wsStatus) {
      case 'connecting':
        return {
          icon: (
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
              className="animate-pulse"
            >
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
          ),
          label: 'Connecting…',
        };
      case 'error':
        return {
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
              <path d="M12 8v4M12 16h.01" />
            </svg>
          ),
          label: 'Connection lost — retrying…',
        };
      case 'closed':
        return {
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M18.36 6.64A9 9 0 0 1 20.77 15M2 12a10 10 0 0 1 18-6" />
              <path d="M2 12a10 10 0 0 0 18 6" />
              <path d="M1 1l22 22" />
            </svg>
          ),
          label: 'Reconnecting…',
        };
      default:
        return {
          icon: (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <circle cx="12" cy="12" r="10" />
            </svg>
          ),
          label: 'Connecting…',
        };
    }
  })();

  return (
    <div
      role="status"
      aria-live="polite"
      className={clsx(
        'sticky top-0 z-20 flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium',
        'bg-amber-100 text-amber-900',
        'dark:bg-amber-900/60 dark:text-amber-100',
      )}
    >
      {config.icon}
      <span>{config.label}</span>
    </div>
  );
}